/**
 * 课程状态模块
 * 管理课程列表、购物车、订单等
 */

import courseApi from '@/api/course'

const state = {
  categoryList: [],         // 课程分类列表
  courseList: [],           // 课程列表
  coursePagination: {       // 课程分页信息
    total: 0,
    per_page: 10,
    current_page: 1,
    last_page: 1,
    has_more: false
  },
  selectedCourses: [],      // 已选课程（购物车）
  myOrders: []              // 我的订单
}

const mutations = {
  /**
   * 设置课程分类列表
   * @param state
   * @param list
   */
  setCategoryList(state, list) {
    state.categoryList = list
  },
  
  /**
   * 设置课程列表
   * @param state
   * @param payload - { list, pagination }
   */
  setCourseList(state, payload) {
    state.courseList = payload.list || []
    if (payload.pagination) {
      state.coursePagination = payload.pagination
    }
  },
  
  /**
   * 添加到购物车
   * @param state
   * @param course
   */
  addToCart(state, course) {
    if (!state.selectedCourses.find(c => c.id === course.id)) {
      state.selectedCourses.push(course)
    }
  },
  
  /**
   * 从购物车移除
   * @param state
   * @param courseId
   */
  removeFromCart(state, courseId) {
    state.selectedCourses = state.selectedCourses.filter(c => c.id !== courseId)
  },
  
  /**
   * 清空购物车
   * @param state
   */
  clearCart(state) {
    state.selectedCourses = []
  },
  
  /**
   * 设置我的订单列表
   * @param state
   * @param orders
   */
  setMyOrders(state, orders) {
    state.myOrders = orders
  }
}

const actions = {
  /**
   * 获取课程分类列表
   * @param commit
   * @param params
   */
  async fetchCategoryList({ commit }, params) {
    const res = await courseApi.getCategoryList(params)
    if (res && res.code === 2000) {
      // API 返回的 data 直接是数组
      commit('setCategoryList', Array.isArray(res.data) ? res.data : (res.data.list || []))
    }
    return res
  },
  
  /**
   * 获取课程列表
   * @param commit
   * @param params
   */
  async fetchCourseList({ commit }, params) {
    const res = await courseApi.getCourseList(params)
    if (res && res.code === 2000) {
      commit('setCourseList', {
        list: res.data.data || [],
        pagination: {
          total: res.data.total || 0,
          per_page: res.data.per_page || 10,
          current_page: res.data.current_page || 1,
          last_page: res.data.last_page || 1,
          has_more: res.data.has_more || false
        }
      })
    }
    return res
  },
  
  /**
   * 购买课程
   * @param commit
   * @param state
   */
  async purchaseCourses({ commit, state }) {
    const courseIds = state.selectedCourses.map(c => c.id)
    const res = await courseApi.purchaseCourses(courseIds)
    if (res && res.code === 2000) {
      commit('clearCart')
    }
    return res
  },
  
  /**
   * 获取我的订单列表
   * @param commit
   * @param params
   */
  async fetchMyOrders({ commit }, params) {
    const res = await courseApi.getMyOrders(params)
    if (res && res.code === 2000) {
      commit('setMyOrders', res.data.list || [])
    }
    return res
  }
}

const getters = {
  categoryList: state => state.categoryList,
  courseList: state => state.courseList,
  coursePagination: state => state.coursePagination,
  selectedCourses: state => state.selectedCourses,
  myOrders: state => state.myOrders,
  
  // 购物车总金额
  cartTotal: state => {
    return state.selectedCourses.reduce((sum, course) => {
      return sum + parseFloat(course.price || 0)
    }, 0)
  },
  
  // 购物车课程数量
  cartCount: state => state.selectedCourses.length,
  
  // 未上课订单数量
  notAttendedCount: state => {
    return state.myOrders.filter(order => order.course_status === 0).length
  },
  
  // 已上课订单数量
  attendedCount: state => {
    return state.myOrders.filter(order => order.course_status === 1).length
  }
}

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters
}
